/**
 * 设定列表组件
 *
 * 显示工作区内的所有设定，支持按名称和级别筛选
 * 点击设定项可触发选中回调，支持删除设定
 * 支持按卷分组显示设定
 * 支持设定名称和卷名直接编辑
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { Setting, SettingLevel, SettingType } from '../../types/setting';
import type { Volume, VolumeCreate, VolumeUpdate } from '../../types/volume';
import { SettingService } from '../../services/setting';
import { volumeApi, OverlappingRangeError, ValidationError, DuplicateNameError } from '../../services/volumeApi';

import { useAuthStore } from '../../store/authStore';
import { useRefreshStore } from '../../store/refreshStore';

// 搜索图标
const SEARCH_ICON = (
  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

// 刷新图标
const REFRESH_ICON = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

// 卷图标
const VOLUME_ICON = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
  </svg>
);

// 折叠图标
const CHEVRON_DOWN_ICON = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const CHEVRON_RIGHT_ICON = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

// 全文级图标
const GLOBAL_ICON = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// SVG图标常量
const DELETE_ICON = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    />
  </svg>
);

const LOCK_ICON = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
    />
  </svg>
);

const UNLOCK_ICON = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z"
    />
  </svg>
);

const ADD_ICON = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const EMPTY_ICON = (
  <svg className="w-16 h-16 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);

const ERROR_ICON = (
  <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const LOADING_SPINNER_LARGE = (
  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-500" />
);

const LOADING_SPINNER_SMALL = (
  <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-gray-500" />
);

const EDIT_ICON = (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
  </svg>
);

// 辅助函数
const getLevelLabel = (level: SettingLevel): string => {
  switch (level) {
    case 'global':
      return '全文级';
    case 'volume':
      return '卷级';
    case 'chapter':
      return '章节级';
    default:
      return level;
  }
};

const getTypeLabel = (type: SettingType): string => {
  const labels: Record<string, string> = {
    charter: '宪章',
    outline: '大纲',
    detail: '细纲',
    summary: '摘要',
    character: '人物',
    supporting: '配角',
    location: '地点',
    item: '物品'
  };
  return labels[type] || type;
};

const formatScope = (level: SettingLevel, scope: string | null): string => {
  if (level === 'global') {
    return '整个工作区';
  }
  if (level === 'chapter') {
    return scope ? `第${scope}章` : '-';
  }
  if (level === 'volume') {
    return scope ? `章节${scope}` : '-';
  }
  return scope || '-';
};

// ==================== 可编辑标题组件 ====================

interface EditableTitleProps {
  title: string;
  onSave: (newTitle: string) => Promise<void> | void;
  className?: string;
  inputClassName?: string;
  maxLength?: number;
}

const EditableTitle: React.FC<EditableTitleProps> = ({
  title,
  onSave,
  className = '',
  inputClassName = '',
  maxLength = 100
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(title);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleClick = () => {
    setIsEditing(true);
    setEditValue(title);
  };

  const handleSave = async () => {
    const trimmed = editValue.trim();
    if (!trimmed || trimmed === title) {
      setIsEditing(false);
      setEditValue(title);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(trimmed);
      setIsEditing(false);
    } catch (error) {
      console.error('保存失败:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue(title);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-1 flex-1">
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value.slice(0, maxLength))}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          disabled={isSaving}
          className={`flex-1 px-2 py-0.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-400 ${inputClassName}`}
        />
        {isSaving && LOADING_SPINNER_SMALL}
      </div>
    );
  }

  return (
    <span
      onClick={handleClick}
      className={`cursor-pointer hover:text-gray-600 hover:bg-gray-100 rounded px-1 -mx-1 transition-colors group ${className}`}
      title="点击修改名称"
    >
      {title}
      <span className="inline-block ml-1 opacity-0 group-hover:opacity-100 text-gray-400">
        {EDIT_ICON}
      </span>
    </span>
  );
};

// ==================== 新增菜单组件 ====================

interface AddMenuProps {
  onAddSetting: () => void;
  onAddVolume: () => void;
}

const AddMenu: React.FC<AddMenuProps> = ({ onAddSetting, onAddVolume }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-2 text-sm bg-gray-800 hover:bg-gray-700 text-white rounded-lg shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-150 flex items-center justify-center gap-2"
      >
        {ADD_ICON}
        新增
      </button>
      
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1">
          <button
            onClick={() => {
              onAddSetting();
              setIsOpen(false);
            }}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            新增设定
          </button>
          <button
            onClick={() => {
              onAddVolume();
              setIsOpen(false);
            }}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2"
          >
            {VOLUME_ICON}
            新增卷
          </button>
        </div>
      )}
    </div>
  );
};

// ==================== 卷表单对话框 ====================

interface VolumeFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: number;
  volumes: Volume[];
  onSuccess: () => void;
  editingVolume?: Volume | null;
}

const VolumeFormDialog: React.FC<VolumeFormDialogProps> = ({
  isOpen,
  onClose,
  workspaceId,
  volumes,
  onSuccess,
  editingVolume
}) => {
  const [formData, setFormData] = useState<VolumeCreate>({
    name: '',
    start_chapter: 1,
    end_chapter: 1
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (editingVolume) {
        setFormData({
          name: editingVolume.name,
          start_chapter: editingVolume.start_chapter,
          end_chapter: editingVolume.end_chapter
        });
      } else {
        // 自动计算下一卷的起始章节
        const maxEndChapter = volumes.length > 0 
          ? Math.max(...volumes.map(v => v.end_chapter))
          : 0;
        const nextStart = maxEndChapter + 1;
        setFormData({
          name: '',
          start_chapter: nextStart,
          end_chapter: nextStart
        });
      }
      setErrors({});
      setFormError(null);
    }
  }, [isOpen, editingVolume, volumes]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = '卷名不能为空';
    } else if (formData.name.trim().length > 100) {
      newErrors.name = '卷名长度不能超过100字符';
    }
    
    if (formData.start_chapter < 1) {
      newErrors.start_chapter = '起始章节不能小于1';
    }
    
    if (formData.end_chapter < 1) {
      newErrors.end_chapter = '终止章节不能小于1';
    }
    
    if (formData.start_chapter > formData.end_chapter) {
      newErrors.end_chapter = '终止章节必须大于等于起始章节';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setFormError(null);

    try {
      if (editingVolume) {
        const updateData: VolumeUpdate = {
          name: formData.name,
          start_chapter: formData.start_chapter,
          end_chapter: formData.end_chapter
        };
        await volumeApi.updateVolume(editingVolume.id, updateData);
      } else {
        await volumeApi.createVolume(workspaceId, formData);
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error('保存卷失败:', error);
      if (error instanceof OverlappingRangeError) {
        setErrors({ end_chapter: error.message });
      } else if (error instanceof DuplicateNameError) {
        setErrors({ name: error.message });
      } else if (error instanceof ValidationError) {
        setFormError(error.message);
      } else {
        setFormError('操作失败，请稍后重试');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-sm w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {editingVolume ? '编辑卷' : '新增卷'}
          </h2>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {formError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              {formError}
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              卷名 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="例如：第一卷：初入江湖"
              maxLength={100}
              disabled={isSubmitting}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400 ${
                errors.name ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                起始章节 <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={formData.start_chapter}
                onChange={(e) => setFormData(prev => ({ ...prev, start_chapter: parseInt(e.target.value) || 1 }))}
                min={1}
                disabled={isSubmitting}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400 ${
                  errors.start_chapter ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.start_chapter && <p className="mt-1 text-sm text-red-600">{errors.start_chapter}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                终止章节 <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={formData.end_chapter}
                onChange={(e) => setFormData(prev => ({ ...prev, end_chapter: parseInt(e.target.value) || 1 }))}
                min={1}
                disabled={isSubmitting}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400 ${
                  errors.end_chapter ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.end_chapter && <p className="mt-1 text-sm text-red-600">{errors.end_chapter}</p>}
            </div>
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm bg-gray-800 hover:bg-gray-700 text-white rounded-md transition-colors disabled:opacity-50"
            >
              {isSubmitting ? '保存中...' : (editingVolume ? '更新' : '创建')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// 筛选栏Props接口
interface FilterBarProps {
  nameFilter: string;
  onNameChange: (value: string) => void;
  onClear: () => void;
  onRefresh?: () => void;
}

// 筛选栏组件
const FilterBar: React.FC<FilterBarProps> = ({
  nameFilter,
  onNameChange,
  onClear: _onClear,
  onRefresh,
}) => {
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onNameChange(e.target.value.slice(0, 100));
  };

  return (
    <div className="p-4 border-b border-gray-200">
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2">
            {SEARCH_ICON}
          </div>
          <input
            type="text"
            placeholder="搜索设定..."
            value={nameFilter}
            onChange={handleNameChange}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-white transition-all"
          />
        </div>
        <button
          onClick={onRefresh}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          title="刷新"
        >
          {REFRESH_ICON}
        </button>
      </div>
    </div>
  );
};

// 设定分组Props接口
interface SettingGroupProps {
  title: string;
  icon: React.ReactNode;
  settings: Setting[];
  selectedSettingId?: number;
  expanded: boolean;
  onToggle: () => void;
  onSelect: (setting: Setting) => void;
  onDelete: (settingId: number) => void;
  onLockToggle?: (setting: Setting) => void;
  lockingSettingId?: number | null;
  count?: number;
  onTitleEdit?: (newTitle: string) => Promise<void>;
  isEditable?: boolean;
  onSettingUpdate?: (settingId: number, newName: string) => Promise<void>;
}

// 设定分组组件
const SettingGroup: React.FC<SettingGroupProps> = ({
  title,
  icon,
  settings,
  selectedSettingId,
  expanded,
  onToggle,
  onSelect,
  onDelete,
  onLockToggle,
  lockingSettingId,
  count,
  onTitleEdit,
  isEditable = false,
  onSettingUpdate
}) => {
  if (settings.length === 0) return null;

  return (
    <div className="mb-2">
      <div
        onClick={onToggle}
        className="flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
      >
        <span className="text-gray-500">
          {expanded ? CHEVRON_DOWN_ICON : CHEVRON_RIGHT_ICON}
        </span>
        <span className="text-gray-600">{icon}</span>
        {isEditable && onTitleEdit ? (
          <EditableTitle
            title={title}
            onSave={onTitleEdit}
            className="font-medium text-gray-800"
            inputClassName="font-medium"
          />
        ) : (
          <span className="font-medium text-gray-800">{title}</span>
        )}
        {count !== undefined && (
          <span className="text-xs text-gray-400 ml-2">({count}个)</span>
        )}
      </div>
      {expanded && (
        <div className="mt-1 space-y-1 border-l-2 border-gray-200 ml-2 pl-2">
          {settings.map(setting => (
            <SettingItem
              key={setting.id}
              setting={setting}
              isSelected={selectedSettingId === setting.id}
              onSelect={onSelect}
              onDelete={onDelete}
              onLockToggle={onLockToggle}
              isLocking={lockingSettingId === setting.id}
              onSettingUpdate={onSettingUpdate}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// 设定列表项Props接口
interface SettingItemProps {
  setting: Setting;
  isSelected: boolean;
  onSelect: (setting: Setting) => void;
  onDelete: (settingId: number) => void;
  onLockToggle?: (setting: Setting) => void;
  isLocking?: boolean;
  onSettingUpdate?: (settingId: number, newName: string) => Promise<void>;
}

// 设定列表项子组件
const SettingItem: React.FC<SettingItemProps> = ({
  setting,
  isSelected,
  onSelect,
  onDelete,
  onLockToggle,
  isLocking,
  onSettingUpdate
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(setting.name);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // 进入编辑模式时聚焦并选中
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleDeleteClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteConfirm(true);
  }, []);

  const handleConfirmDelete = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(setting.id);
    setShowDeleteConfirm(false);
  }, [setting.id, onDelete]);

  const handleCancelDelete = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteConfirm(false);
  }, []);

  const handleLockClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (onLockToggle && !isLocking) {
      onLockToggle(setting);
    }
  }, [setting, onLockToggle, isLocking]);

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    // 双击进入编辑模式
    if (onSettingUpdate) {
      setIsEditing(true);
      setEditValue(setting.name);
    }
  }, [setting.name, onSettingUpdate]);

  const handleSave = async () => {
    const trimmed = editValue.trim();
    if (!trimmed || trimmed === setting.name) {
      setIsEditing(false);
      setEditValue(setting.name);
      return;
    }

    setIsSaving(true);
    try {
      await onSettingUpdate?.(setting.id, trimmed);
      setIsEditing(false);
    } catch (error) {
      console.error('保存设定名称失败:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue(setting.name);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <div
      onClick={() => onSelect(setting)}
      onDoubleClick={handleDoubleClick}
      className={`
        relative p-3 rounded-xl cursor-pointer transition-all duration-150
        ${isSelected ? 'bg-gray-100 border-l-4 border-gray-800' : 'hover:bg-gray-50 border-l-4 border-transparent'}
      `}
      title={onSettingUpdate ? '双击重命名设定' : undefined}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <div className="flex items-center gap-1">
              <input
                ref={inputRef}
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value.slice(0, 100))}
                onKeyDown={handleKeyDown}
                onBlur={handleSave}
                disabled={isSaving}
                className="flex-1 px-2 py-0.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-400 font-medium"
                onClick={(e) => e.stopPropagation()}
              />
              {isSaving && LOADING_SPINNER_SMALL}
            </div>
          ) : (
            <h3 className={`font-medium truncate ${isSelected ? 'text-gray-900' : 'text-gray-800'}`}>
              {setting.name}
            </h3>
          )}
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded-md">
              {getLevelLabel(setting.level)}
            </span>
            <span className="text-xs px-2 py-0.5 bg-paper-50 text-paper-800 rounded-md">
              {getTypeLabel(setting.setting_type)}
            </span>
          </div>
        </div>

        {!showDeleteConfirm && (
          <div className="flex gap-1">
            {onLockToggle && (
              <button
                onClick={handleLockClick}
                disabled={isLocking}
                className={`p-1 transition-colors rounded-lg ${
                  setting.is_locked
                    ? 'text-amber-600 hover:bg-amber-50'
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                } ${isLocking ? 'opacity-50 cursor-wait' : ''}`}
                title={setting.is_locked ? '解锁设定（AI将无法修改）' : '锁定设定（AI将无法修改）'}
              >
                {isLocking ? LOADING_SPINNER_SMALL : setting.is_locked ? LOCK_ICON : UNLOCK_ICON}
              </button>
            )}
            <button
              onClick={handleDeleteClick}
              className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="删除设定"
            >
              {DELETE_ICON}
            </button>
          </div>
        )}
      </div>

      <div className="text-xs text-gray-500">
        作用范围: {formatScope(setting.level, setting.scope)}
      </div>

      {showDeleteConfirm && (
        <div className="absolute inset-0 bg-white rounded-xl flex items-center justify-center p-3 z-10">
          <div className="text-center">
            <p className="text-sm text-gray-900 mb-3">确认删除此设定？</p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={handleCancelDelete}
                className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-3 py-1.5 text-sm bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
              >
                删除
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 组件Props接口
interface SettingListProps {
  workspaceId: number;
  selectedSettingId?: number;
  onSettingSelect?: (setting: Setting) => void;
  onSettingDelete?: (settingId: number) => void;
  onSettingCreate?: () => void;
  onSettingsChange?: (settings: Setting[]) => void;
  settingService?: SettingService;
}

export const SettingList: React.FC<SettingListProps> = ({
  workspaceId,
  selectedSettingId,
  onSettingSelect,
  onSettingDelete,
  onSettingCreate,
  onSettingsChange,
  settingService: externalSettingService
}) => {
  const [settings, setSettings] = useState<Setting[]>([]);
  const [filteredSettings, setFilteredSettings] = useState<Setting[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nameFilter, setNameFilter] = useState('');
  const [isDeleting, setIsDeleting] = useState<number | null>(null);
  const [lockingSettingId, setLockingSettingId] = useState<number | null>(null);
  
  // 卷相关状态
  const [volumes, setVolumes] = useState<Volume[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['global']));
  const [showVolumeDialog, setShowVolumeDialog] = useState(false);
  const [editingVolume, setEditingVolume] = useState<Volume | null>(null);

  // 使用 ref 标记是否正在刷新，防止重复刷新
  const isRefreshingRef = useRef(false);

  // 标记首次加载是否已完成，防止挂载时重复加载
  const hasInitiallyLoadedRef = useRef(false);

  // 获取 token
  const { token } = useAuthStore();
  const { settingsRefreshKey, lastUpdatedSettingId } = useRefreshStore();

  // 使用useRef存储SettingService实例，确保token变化时重新创建
  const settingServiceRef = useRef<SettingService | null>(null);
  const tokenRef = useRef<string | null>(null);

  // 当token或workspaceId变化时重新创建SettingService
  if (!settingServiceRef.current || token !== tokenRef.current) {
    tokenRef.current = token;
    settingServiceRef.current = externalSettingService || new SettingService(
      undefined,
      token ?? undefined,
      workspaceId
    );
  }

  // 使用ref.current进行所有操作
  const settingService = settingServiceRef.current;

  // 加载卷列表
  const loadVolumes = useCallback(async () => {
    if (!workspaceId) return;
    
    try {
      const data = await volumeApi.listVolumes(workspaceId);
      const sortedVolumes = data.sort((a, b) => a.start_chapter - b.start_chapter);
      setVolumes(sortedVolumes);
      
      // 默认展开所有卷组
      const volumeGroupIds = sortedVolumes.map(v => `volume-${v.id}`);
      setExpandedGroups(prev => new Set([...prev, ...volumeGroupIds]));
    } catch (err) {
      console.error('[SettingList] 加载卷列表失败:', err);
    }
  }, [workspaceId]);

  // 切换分组展开状态
  const toggleGroupExpand = useCallback((groupId: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  }, []);

  // 处理卷更新
  const handleVolumeUpdate = useCallback(async (volumeId: number, data: VolumeUpdate) => {
    try {
      await volumeApi.updateVolume(volumeId, data);
      await loadVolumes();
    } catch (error) {
      console.error('更新卷失败:', error);
      throw error;
    }
  }, [loadVolumes]);

  const loadSettings = useCallback(async () => {
    if (!workspaceId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await settingService.listSettings(workspaceId);
      setSettings(data);
      setFilteredSettings(data);
      onSettingsChange?.(data);
    } catch (err) {
      console.error('[SettingList] 加载失败:', err);
      setError('加载设定列表失败，请重试');
    } finally {
      setLoading(false);
    }
  }, [workspaceId, settingService, onSettingsChange]);

  useEffect(() => {
    // 使用 cancelled 标志防止组件卸载后的状态更新
    let cancelled = false;

    const doLoadSettings = async () => {
      if (!workspaceId) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const data = await settingService.listSettings(workspaceId);
        
        // 检查组件是否已卸载
        if (!cancelled) {
          setSettings(data);
          setFilteredSettings(data);
          onSettingsChange?.(data);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[SettingList] 加载失败:', err);
          setError('加载设定列表失败，请重试');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    // 首次加载数据
    doLoadSettings();
    loadVolumes();
    hasInitiallyLoadedRef.current = true;

    // 清理函数：设置 cancelled 标志
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 监听刷新事件
  useEffect(() => {
    // 首次加载由第一个 useEffect 处理，这里只响应后续刷新
    if (!hasInitiallyLoadedRef.current) {
      return;
    }

    // 移除 settings.length > 0 条件限制，支持空列表刷新
    // 使用 isRefreshingRef 防止重复刷新请求
    if (!loading && !isRefreshingRef.current) {
      isRefreshingRef.current = true;
      loadSettings().finally(() => {
        isRefreshingRef.current = false;
      });

      // 如果当前选中的设定被更新，触发详情刷新
      if (lastUpdatedSettingId && selectedSettingId === lastUpdatedSettingId) {
        const updatedSetting = settings.find(s => s.id === lastUpdatedSettingId);
        if (updatedSetting) {
          onSettingSelect?.(updatedSetting);
        }
      }
    }
  }, [settingsRefreshKey]); // 只依赖刷新键

  const handleSettingSelect = useCallback((setting: Setting) => {
    onSettingSelect?.(setting);
  }, [onSettingSelect]);

  const handleSettingDelete = useCallback(async (settingId: number) => {
    if (isDeleting === settingId) {
      return;
    }

    setIsDeleting(settingId);

    try {
      await settingService.deleteSetting(workspaceId, settingId);
      onSettingDelete?.(settingId);
      await loadSettings();
    } catch (err) {
      let errorMessage = '删除失败，请重试';
      console.error('[SettingList] 删除失败:', err);
      setError(errorMessage);
    } finally {
      setIsDeleting(null);
    }
  }, [workspaceId, settingService, onSettingDelete, loadSettings, isDeleting]);

  const handleCreateSetting = useCallback(() => {
    onSettingCreate?.();
  }, [onSettingCreate]);

  const handleLockToggle = useCallback(async (setting: Setting) => {
    if (lockingSettingId === setting.id) {
      return;
    }

    setLockingSettingId(setting.id);

    try {
      const action = setting.is_locked ? 'unlock' : 'lock';
      const updatedSetting = await settingService.lockSetting(workspaceId, setting.id, action);

      // 更新本地状态
      setSettings(prevSettings =>
        prevSettings.map(s => s.id === setting.id ? updatedSetting : s)
      );

      // 如果当前选中的设定被更新，触发选中回调
      if (selectedSettingId === setting.id) {
        onSettingSelect?.(updatedSetting);
      }

      // 通知外部设置已变更
      onSettingsChange?.(
        settings.map(s => s.id === setting.id ? updatedSetting : s)
      );
    } catch (err) {
      console.error('[SettingList] 锁定操作失败:', err);
      setError(setting.is_locked ? '解锁失败，请重试' : '锁定失败，请重试');
    } finally {
      setLockingSettingId(null);
    }
  }, [workspaceId, settingService, selectedSettingId, onSettingSelect, onSettingsChange, settings, lockingSettingId]);

  const handleClearFilters = useCallback(() => {
    setNameFilter('');
    setFilteredSettings(settings);
  }, [settings]);

  // 处理搜索输入变化
  const handleSearchChange = useCallback((value: string) => {
    setNameFilter(value);
    
    if (value.trim() === '') {
      setFilteredSettings(settings);
    } else {
      const filtered = settings.filter(setting => 
        setting.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredSettings(filtered);
    }
  }, [settings]);

  // 处理新增卷
  const handleAddVolume = useCallback(() => {
    setEditingVolume(null);
    setShowVolumeDialog(true);
  }, []);

  // 处理卷保存成功
  const handleVolumeSuccess = useCallback(() => {
    loadVolumes();
  }, [loadVolumes]);

  // 处理设定名称更新（双击重命名）
  const handleSettingNameUpdate = useCallback(async (settingId: number, newName: string) => {
    try {
      const updatedSetting = await settingService.updateSetting(workspaceId, settingId, { name: newName });
      // 更新本地状态
      setSettings(prev => prev.map(s => s.id === settingId ? updatedSetting : s));
      setFilteredSettings(prev => prev.map(s => s.id === settingId ? updatedSetting : s));
      // 通知外部设置已变更
      onSettingsChange?.(
        settings.map(s => s.id === settingId ? updatedSetting : s)
      );
    } catch (error) {
      console.error('更新设定名称失败:', error);
      throw error;
    }
  }, [workspaceId, settingService, onSettingsChange, settings]);

  // 加载状态
  if (loading && settings.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          {LOADING_SPINNER_LARGE}
          <p className="mt-2 text-sm text-gray-600">加载中...</p>
        </div>
      </div>
    );
  }

  // 错误状态
  if (error && settings.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center text-red-600">
          {ERROR_ICON}
          <p className="text-sm">{error}</p>
          <button
            onClick={loadSettings}
            className="mt-3 px-4 py-2 text-sm bg-gray-800 hover:bg-gray-700 text-white rounded-lg shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-150"
          >
            重试
          </button>
        </div>
      </div>
    );
  }

  // 空状态
  if (settings.length === 0 && !loading) {
    return (
      <>
        <div className="h-full flex flex-col">
          <FilterBar
            nameFilter={nameFilter}
            onNameChange={handleSearchChange}
            onClear={handleClearFilters}
            onRefresh={loadSettings}
          />

          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              {EMPTY_ICON}
              <p className="text-sm text-gray-600 mb-3">
                {nameFilter ? '没有找到匹配的设定' : '暂无设定'}
              </p>
              {!nameFilter && onSettingCreate && (
                <AddMenu
                  onAddSetting={handleCreateSetting}
                  onAddVolume={handleAddVolume}
                />
              )}
            </div>
          </div>
        </div>

        {/* 卷表单对话框 */}
        <VolumeFormDialog
          isOpen={showVolumeDialog}
          onClose={() => setShowVolumeDialog(false)}
          workspaceId={workspaceId}
          volumes={volumes}
          onSuccess={handleVolumeSuccess}
          editingVolume={editingVolume}
        />
      </>
    );
  }

  // 正常状态
  
  // 分组设定
  const globalSettings = filteredSettings.filter(s => s.level === 'global');
  
  // 卷级和章节级设定按卷分组
  const getVolumeSettings = (volume: Volume) => {
    return filteredSettings.filter(s => {
      if (s.level === 'volume') {
        // 卷级设定：scope 格式为 "1-20"，需要检查是否与卷范围匹配
        if (s.scope) {
          const [start, end] = s.scope.split('-').map(Number);
          return start === volume.start_chapter && end === volume.end_chapter;
        }
        return false;
      }
      if (s.level === 'chapter') {
        // 章节级设定：检查章节是否在卷范围内
        if (s.scope) {
          const chapterNum = parseInt(s.scope, 10);
          return chapterNum >= volume.start_chapter && chapterNum <= volume.end_chapter;
        }
        return false;
      }
      return false;
    });
  };
  
  // 未匹配到卷的设定
  const getUnmatchedSettings = () => {
    return filteredSettings.filter(s => {
      if (s.level === 'global') return false;
      if (s.level === 'volume') {
        if (!s.scope) return true;
        const [start, end] = s.scope.split('-').map(Number);
        return !volumes.some(v => v.start_chapter === start && v.end_chapter === end);
      }
      if (s.level === 'chapter') {
        if (!s.scope) return true;
        const chapterNum = parseInt(s.scope, 10);
        return !volumes.some(v => chapterNum >= v.start_chapter && chapterNum <= v.end_chapter);
      }
      return false;
    });
  };

  return (
    <>
      <div className="h-full flex flex-col">
        <FilterBar
          nameFilter={nameFilter}
          onNameChange={handleSearchChange}
          onClear={handleClearFilters}
          onRefresh={loadSettings}
        />

        {onSettingCreate && (
          <div className="p-4 border-b border-gray-200">
            <AddMenu
              onAddSetting={handleCreateSetting}
              onAddVolume={handleAddVolume}
            />
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {nameFilter ? (
            // 搜索模式：平铺显示
            filteredSettings.map((setting) => (
              <SettingItem
                key={setting.id}
                setting={setting}
                isSelected={selectedSettingId === setting.id}
                onSelect={handleSettingSelect}
                onDelete={handleSettingDelete}
                onLockToggle={handleLockToggle}
                isLocking={lockingSettingId === setting.id}
                onSettingUpdate={handleSettingNameUpdate}
              />
            ))
          ) : (
            // 正常模式：分组显示
            <>
              {/* 全文级设定 */}
              <SettingGroup
                title="全文级设定"
                icon={GLOBAL_ICON}
                settings={globalSettings}
                selectedSettingId={selectedSettingId}
                expanded={expandedGroups.has('global')}
                onToggle={() => toggleGroupExpand('global')}
                onSelect={handleSettingSelect}
                onDelete={handleSettingDelete}
                onLockToggle={handleLockToggle}
                lockingSettingId={lockingSettingId}
                count={globalSettings.length}
                onSettingUpdate={handleSettingNameUpdate}
              />
              
              {/* 按卷分组 */}
              {volumes.map((volume) => {
                const volumeSettings = getVolumeSettings(volume);
                return (
                  <SettingGroup
                    key={volume.id}
                    title={volume.name}
                    icon={VOLUME_ICON}
                    settings={volumeSettings}
                    selectedSettingId={selectedSettingId}
                    expanded={expandedGroups.has(`volume-${volume.id}`)}
                    onToggle={() => toggleGroupExpand(`volume-${volume.id}`)}
                    onSelect={handleSettingSelect}
                    onDelete={handleSettingDelete}
                    onLockToggle={handleLockToggle}
                    lockingSettingId={lockingSettingId}
                    count={volumeSettings.length}
                    isEditable={true}
                    onTitleEdit={(newName) => handleVolumeUpdate(volume.id, { name: newName, start_chapter: volume.start_chapter, end_chapter: volume.end_chapter })}
                    onSettingUpdate={handleSettingNameUpdate}
                  />
                );
              })}
              
              {/* 未匹配的设定 */}
              {getUnmatchedSettings().length > 0 && (
                <SettingGroup
                  title="其他设定"
                  icon={VOLUME_ICON}
                  settings={getUnmatchedSettings()}
                  selectedSettingId={selectedSettingId}
                  expanded={expandedGroups.has('unmatched')}
                  onToggle={() => toggleGroupExpand('unmatched')}
                  onSelect={handleSettingSelect}
                  onDelete={handleSettingDelete}
                  onLockToggle={handleLockToggle}
                  lockingSettingId={lockingSettingId}
                  count={getUnmatchedSettings().length}
                  onSettingUpdate={handleSettingNameUpdate}
                />
              )}
            </>
          )}
          {filteredSettings.length === 0 && nameFilter && (
            <div className="text-center py-8 text-gray-500 text-sm">
              没有找到匹配的设定
            </div>
          )}
        </div>

        {loading && settings.length > 0 && (
          <div className="p-4 text-center">
            {LOADING_SPINNER_SMALL}
          </div>
        )}

        {!loading && (
          <div className="p-4 border-t border-gray-200 text-center text-xs text-gray-500">
            共 {filteredSettings.length} 个设定
          </div>
        )}
      </div>

      {/* 卷表单对话框 */}
      <VolumeFormDialog
        isOpen={showVolumeDialog}
        onClose={() => setShowVolumeDialog(false)}
        workspaceId={workspaceId}
        volumes={volumes}
        onSuccess={handleVolumeSuccess}
        editingVolume={editingVolume}
      />
    </>
  );
};

export default SettingList;
